import type { Core } from '@pdftron/webviewer';
import { cloneDeep } from 'lodash';
import { ApryseSDK } from './ApryseSDK';
import { devLog, devWarning } from '../utils/developmentUtils';
import { arraysAreEqual } from '../utils/arrayUtils';

export type ISpecificChangeInfo =
    | 'color'
    | 'fillColor'
    | 'lineStyle'
    | 'lineCloudyStyle'
    | 'lineWidth'
    | 'lineStart'
    | 'lineEnd'
    | 'unknown';

export interface IAnnotationHistoryEntry {
    annotations: Core.Annotations.Annotation[];
    action: 'add' | 'modify' | 'delete';
    specificInfo?: ISpecificChangeInfo;
    snapshotBefore?: Core.Annotations.Annotation[];
    snapshotAfter?: Core.Annotations.Annotation[];
}

export class CustomHistoryManager {
    static savedTime: number;
    static savedIds: string[] = [];
    static undoRedoInProgress = false;
    private static _annotationHistory: IAnnotationHistoryEntry[] = [];
    private static _annotationHistoryIdx = -1;

    static takeAnnotationsSnapshot(): Core.Annotations.Annotation[] {
        return cloneDeep(ApryseSDK.annotationManager.getAnnotationsList());
    }

    static areAllAnnotationsSaved(): boolean {
        const annotations = ApryseSDK.annotationManager.getAnnotationsList();

        if (
            !arraysAreEqual(
                CustomHistoryManager.savedIds,
                annotations.map(a => a.Id),
            )
        ) {
            return false;
        }

        return annotations.every(a => {
            const modifiedTime = a.DateModified?.getTime() ?? 0;
            return modifiedTime <= CustomHistoryManager.savedTime;
        });
    }

    static canUndo(): boolean {
        return this._annotationHistory.length > 0 && this._annotationHistoryIdx !== -1;
    }

    static canRedo(): boolean {
        return (
            this._annotationHistory.length > 0 &&
            this._annotationHistoryIdx < this._annotationHistory.length - 1
        );
    }

    static get annotationHistory(): IAnnotationHistoryEntry[] {
        return this._annotationHistory;
    }

    static getPrevHistoryAction(): IAnnotationHistoryEntry | undefined {
        return this._annotationHistory[this._annotationHistoryIdx];
    }

    static getNextHistoryAction(): IAnnotationHistoryEntry | undefined {
        return this._annotationHistory[this._annotationHistoryIdx + 1];
    }

    static appendHistoryAction(entry: IAnnotationHistoryEntry): void {
        devLog('History entry', entry);
        if (this._annotationHistoryIdx !== this._annotationHistory.length - 1) {
            this.historyFlushRedoActions();
        }
        this._annotationHistory.push(entry);
        this._annotationHistoryIdx++;
    }

    static historyStepBack(): void {
        this._annotationHistoryIdx--;
    }

    static historyStepForth(): void {
        this._annotationHistoryIdx++;
    }

    static historyFlushRedoActions(): void {
        this._annotationHistory = this._annotationHistory.slice(0, this._annotationHistoryIdx + 1);
        this._annotationHistoryIdx = this._annotationHistory.length - 1;
    }

    static undo(historyEntry: IAnnotationHistoryEntry): void {
        devLog('CustomHistoryManager.undo');

        CustomHistoryManager.undoRedoInProgress = true;

        CustomHistoryManager.restoreHistoryState(historyEntry, 'undo');
        CustomHistoryManager.historyStepBack();

        CustomHistoryManager.undoRedoInProgress = false;
    }

    static redo(historyEntry: IAnnotationHistoryEntry): void {
        devLog('CustomHistoryManager.redo');

        CustomHistoryManager.undoRedoInProgress = true;

        CustomHistoryManager.restoreHistoryState(historyEntry, 'redo');
        CustomHistoryManager.historyStepForth();

        CustomHistoryManager.undoRedoInProgress = false;
    }

    private static restoreHistoryState(entry: IAnnotationHistoryEntry, mode: 'undo' | 'redo') {
        CustomHistoryManager.restoreCreation(entry, mode) ||
            CustomHistoryManager.restoreDeletion(entry, mode) ||
            CustomHistoryManager.restoreModification(entry, mode);
    }

    private static restoreCreation(entry: IAnnotationHistoryEntry, mode: 'undo' | 'redo'): boolean {
        if (
            (entry.action === 'delete' && mode === 'undo') ||
            (entry.action === 'add' && mode === 'redo')
        ) {
            ApryseSDK.annotationManager.addAnnotations(entry.annotations, {
                isUndoRedo: true,
            });
            return true;
        }
        return false;
    }

    private static restoreDeletion(entry: IAnnotationHistoryEntry, mode: 'undo' | 'redo'): boolean {
        if (
            (entry.action === 'add' && mode === 'undo') ||
            (entry.action === 'delete' && mode === 'redo')
        ) {
            const _delete = ApryseSDK.annotationManager
                .getAnnotationsList()
                .filter(annot => entry.annotations.some(_delete => _delete.Id === annot.Id));

            ApryseSDK.annotationManager.deleteAnnotations(_delete, {
                isUndoRedo: true,
                force: true,
            });
            return true;
        }
        return false;
    }

    private static restoreModification(
        entry: IAnnotationHistoryEntry,
        mode: 'undo' | 'redo',
    ): boolean {
        if (entry.action === 'modify') {
            const snapshot = mode === 'undo' ? entry.snapshotBefore : entry.snapshotAfter;

            if (!snapshot) {
                devWarning('Cannot undo/redo modification. (Reason: snapshot missing.)');
                return true;
            }

            const _recreate = snapshot.filter(x =>
                entry.annotations.some(affected => x.Id === affected.Id),
            );

            if (!_recreate.length) {
                devWarning('Cannot undo/redo modification. (Reason: snapshot faulty.)');
                return true;
            }

            const _delete = ApryseSDK.annotationManager
                .getAnnotationsList()
                .filter(annot => entry.annotations.some(_delete => _delete.Id === annot.Id));

            ApryseSDK.annotationManager.deleteAnnotations(_delete, {
                isUndoRedo: true,
                force: true,
            });

            ApryseSDK.annotationManager.addAnnotations(_recreate, {
                isUndoRedo: true,
            });
            return true;
        }
        return false;
    }
}
